import { Attr, BelongsTo, HasMany, HasOne, Model } from "spraypaint"

import { Address } from "./address"
import ApplicationRecord from "./applicationRecord/applicationRecord"
import type { Currency } from "./currency"
import type { Department } from "./department"
// Cannot yet be imported as a type
// because the class must be imported somewhere for it to be added to the modelRegistry
// See spraypaint.js/src/decorators.js -> modelFactory() -> ModelClass.registerType()
import { Location } from "./location"
import type { Organization } from "./organization"
import type { OrganizationalUnit } from "./organizationalUnit"
import { PhysicalDetail } from "./physcialDetails"

export interface SiteInterface {
  readonly carbonEquivalentEmissionsGoal: number
  readonly lineLossFactor: number
  readonly locationId: string
  readonly name: string
  readonly tariffDollars: number
}

interface CustomVariablesInterface {
  description: string | null
  name: string
  value: number
}

export interface ISitePreviewDto {
  departmentName: string | null
  electricityType: string
  siteId: number
  siteName: string
  timezone: string
  topOrgUnitName: string
}

export interface ISitePreview {
  departmentName: string | null
  electricityType: string
  id: string
  name: string
  timezone: string
  topOrgUnitName: string
}

export interface ISiteCounts {
  billingGroups: number
  bills: number
  siteEmissions: number
}

@Model()
export class Site extends ApplicationRecord {
  public static readonly jsonapiType = "sites"

  @BelongsTo("currencies") public currency: Currency | undefined

  // Cannot yet be written as a string (e.g. @BelongsTo("locations"))
  // because the class must be imported somewhere for it to be added to the modelRegistry
  @BelongsTo(Location) public location!: Location

  @BelongsTo("organizations") public organization!: Organization

  @BelongsTo("organizational_units")
  public organizationalUnit!: OrganizationalUnit

  @BelongsTo("departments") public department!: Department

  // Cannot yet be written as a string (e.g. @BelongsTo("addresses"))
  // because the class must be imported somewhere for it to be added to the modelRegistry
  @HasOne(Address)
  public address: Address | undefined

  // Cannot yet be written as a string (e.g. @BelongsTo("physical_details"))
  // because the class must be imported somewhere for it to be added to the modelRegistry
  @HasMany(PhysicalDetail)
  public physicalDetails: PhysicalDetail[] | undefined

  @Attr() public readonly dateCreated!: string

  @Attr() public readonly dateUpdated!: string

  @Attr() public readonly granularity!: string

  @Attr() public readonly lineLossFactor!: number

  @Attr() public readonly notes!: string

  @Attr() public readonly tariffDollars!: number

  @Attr() public readonly tariffMajor!: number

  @Attr() public yearBuilt?: number

  @Attr() public name!: string

  @Attr() public readonly baCode!: string

  @Attr() public readonly timezone!: string

  @Attr() public locationId?: string

  @Attr() public readonly carbonEquivalentEmissionsGoal!: number

  @Attr() public readonly organizationId!: number

  @Attr() public readonly organizationalUnitId!: number

  @Attr() public readonly electricityType!: string

  @Attr() public readonly departmentId!: number

  @Attr() public readonly departmentName!: string

  @Attr() public readonly hasAmiMeter!: boolean

  @Attr() public readonly hasAmrMeter!: boolean

  @Attr() public readonly hasSubMeter!: boolean

  @Attr() public readonly probeMeter!: boolean

  @Attr() public readonly customVariables!: CustomVariablesInterface[]

  @Attr() public readonly locationName!: string

  @Attr() public readonly billingGroupNames!: string[]

  @Attr() public readonly siteAlias!: string

  @Attr() public sqFt?: string | null

  @Attr() public numberOfFloors?: number

  @Attr() public buildingType?: string | null

  isBundledEnergyCustomer(): boolean {
    return this.electricityType === "bundled"
  }
}
